import { Injectable } from '@angular/core';
import { debounce } from 'lodash';
import { Subscription } from 'rxjs';
import { Geolocation, PositionOptions } from '@capacitor/geolocation';
import { environment } from 'src/environments/environment';
import { LatLngBounds } from '@capacitor/google-maps';
import { Loader } from '@googlemaps/js-api-loader';

import { PositionedDeviceData, Position } from 'src/app/model';
import { NativeMapsService } from './native-maps.service';
import { MapsUtils } from './maps-helper.service';
import { CustomMarkers, LatLon, CustomMarker } from 'src/app/model/maps';
import { LocaLstorageService } from 'src/app/store/localstorage.service';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { DeviceBindStoreService } from 'src/app/store/device-bind-store.service';
import { GroupBindStoreService } from 'src/app/store/group-bind-store.service';
import { LatLng } from 'src/app/types/googlemap.capacitor.types';
import { Capacitor } from '@capacitor/core';
import { MapUiTweaks } from './map-ui-tweaks.plugin';

/**
 * `enableHighAccuracy` is what makes @capacitor/geolocation ask for the
 * ACCESS_FINE_LOCATION alias on Android 12+; without it the OS returns an
 * approximate fix. `maximumAge: 0` forbids a cached position.
 */
export const PRECISE_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};

const HIDE_BUTTON_ATTEMPTS = 6;
const HIDE_BUTTON_RETRY_DELAY = 500;

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  private map: NativeMapsService;
  private native = false;
  public subscription: Subscription;
  private boundMapSubscription: Subscription;
  public changes = true;
  private readonly visible = 300;
  private devices: PositionedDeviceData[] = [];
  private devicesCount = 0;
  private positions: Position[] = [];
  private positionsCount = 0;
  private markersIsBusy = true;

  private showMarkers = debounce(this.updateMarkers, 500);

  constructor(
    public utils: MapsUtils,
    private dmStore: DmStoreService,
    private locaSlorageService: LocaLstorageService,
    private deviceBind: DeviceBindStoreService,
    private groupBind: GroupBindStoreService,
  ) { }

  public async init(location: LatLng, element: HTMLElement) {
    this.map = new NativeMapsService(this.dmStore, this.locaSlorageService);
    // Same check HelperProvider.isNative uses. Platform.is('capacitor') returns
    // false here even on device, so it must not be used (native-detection trap
    // in CLAUDE.md).
    this.native = Capacitor.isNativePlatform();

    // Only the web implementation reads this key — it feeds importGoogleLib().
    // The Android map takes its key from AndroidManifest.xml instead.
    const apiKey = this.native ? environment.GoogleMapApiKey : environment.GoogleMapApiKeyWeb;

    await this.map.init(location, element, apiKey);

    this.deviceBind.accurateDevicePosition = false;
    this.groupBind.accuratePositions = false;

    this.locaSlorageService.mapType$.subscribe((mapType) => this.map.setType(mapType));
    this.locaSlorageService.markersType$.subscribe(this.updateMarkersView.bind(this));

    this.subscription = this.dmStore.markers$.subscribe(([devices, positions]) => {
      this.devicesCount = devices.length;
      this.positionsCount = positions.length;
      this.devices = devices;
      this.positions = positions;

      if (!this.devicesCount) {
        this.map.clearDevicesMarkers();
      }
      if (!this.positionsCount) {
        this.map.clearPositionsMarkers();
      }

      if (!this.devicesCount && !this.positionsCount) {
        return false;
      }

      this.showMarkers();

      const devicesLatLng = this.devices.filter(({ lat, lon }) => lat && lon).map(({ lat, lon }) => ({ lat: lat as number, lon: lon as number }));
      const positionsLatLng = this.positions.filter(({ lat, lon }) => lat && lon).map(({ lat, lon }) => ({ lat: lat as number, lon: lon as number }));

      if (this.changes) {
        this.changes = false;
        this.map.bound([...devicesLatLng, ...positionsLatLng]);
      }

      return true;
    });

    this.boundMapSubscription = this.dmStore.boundMap$.subscribe((bound) => {
      if (bound) {
        this.map.bound([...this.devices.map(({ lat, lon }) => ({ lat: lat as number, lon: lon as number })), ...this.positions.map(({ lat, lon }) => ({ lat: lat as number, lon: lon as number }))]);
        this.dmStore.stopBoundMapSignal();
      }
    });

    this.map.dragEnd(() => !this.markersIsBusy && this.showMarkers());

    this.markersIsBusy = false;

    const loader = new Loader({
      apiKey: environment.GoogleMapApiKeyWeb,
      version: 'weekly',
      libraries: ['places', 'geometry'],
    });

    // load google maps to window anyway. This is required in native mode
    if (!window.google) {
      await loader.importLibrary('maps');
    }

    await this.ensurePreciseLocationPermission();
    await this.syncMyLocationLayer();
  }

  /**
   * Asks for the precise-location permission alias explicitly.
   *
   * Without `enableHighAccuracy`, @capacitor/geolocation requests only
   * ACCESS_COARSE_LOCATION on Android 12+, and Android then answers with a
   * deliberately coarsened, stable coordinate ~1-2 km from the real one.
   */
  private async ensurePreciseLocationPermission(): Promise<boolean> {
    try {
      const status = await Geolocation.checkPermissions();

      if (status.location === 'granted') {
        return true;
      }

      const requested = await Geolocation.requestPermissions({ permissions: ['location'] });

      return requested.location === 'granted';
    } catch (error) {
      console.error('Geolocation error');
      return false;
    }
  }

  /**
   * Shows the built-in live location dot only while the precise permission is
   * granted, and hides it again as soon as the user revokes it.
   */
  public async syncMyLocationLayer(): Promise<boolean> {
    let granted = false;

    try {
      const status = await Geolocation.checkPermissions();
      granted = status.location === 'granted';
    } catch (error) {
      console.error('Geolocation error');
    }

    await this.map.enableCurrentLocation(granted);

    if (granted && this.native) {
      await this.hideNativeMyLocationButton();
    }

    return granted;
  }

  /**
   * The Maps SDK draws its own "My Location" button together with the dot, and
   * this app already has a styled "My position" control. Play Services creates
   * that button asynchronously right after the layer is switched on, so retry
   * a few times before giving up.
   */
  private async hideNativeMyLocationButton(): Promise<void> {
    for (let attempt = 1; attempt <= HIDE_BUTTON_ATTEMPTS; attempt++) {
      try {
        const result = await MapUiTweaks.hideMyLocationButton();

        if (result.hidden) {
          return;
        }

        if (attempt === HIDE_BUTTON_ATTEMPTS) {
          console.warn(`[MapUiTweaks] ${result.reason}; view tree:`, JSON.stringify(result.tree));
        }
      } catch (error) {
        console.warn('[MapUiTweaks] plugin call failed:', error);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, HIDE_BUTTON_RETRY_DELAY));
    }
  }

  public setOptionsForAccurate(status = false) {
    this.map.setOptionsForAccurate(status);
  }

  public async getCurrentPosition(): Promise<void | LatLon> {
    try {
      const position = await Geolocation.getCurrentPosition(PRECISE_POSITION_OPTIONS);

      if (position) {
        return { lat: position.coords.latitude, lon: position.coords.longitude };
      }
    } catch (error) {
      console.error('Geolocation error');
    }
  }

  public drawMarkers(markers: CustomMarkers) {
    this.map.drawCustomMarkers(markers);
  }

  public clearCustomMarkers() {
    this.map.clearCustomMarkers();
  }

  public moveMarkers(markers: CustomMarkers) {
    this.map.moveMarkers(markers);
  }

  private async updateMarkers() {
    const visibleRegion: LatLngBounds = await this.map.getVisible();
    const devices = this.reduceMarkersList<PositionedDeviceData>(this.devices, this.devicesCount, visibleRegion);
    const positions = this.reduceMarkersList<Position>(this.positions, this.positionsCount, visibleRegion);

    this.markersIsBusy = true;

    await this.map.deleteUnnecessaryMarkers(devices, positions);

    await this.map.addDevices(devices);
    await this.map.addPositions(positions);

    this.markersIsBusy = false;
  }

  private async updateMarkersView() {
    this.markersIsBusy = true;
    const visibleRegion: LatLngBounds = await this.map.getVisible();
    const devices = this.reduceMarkersList<PositionedDeviceData>(this.devices, this.devicesCount, visibleRegion);
    const positions = this.reduceMarkersList<Position>(this.positions, this.positionsCount, visibleRegion);

    await this.map.switchDevicesView(devices, positions);

    this.markersIsBusy = false;
  }

  // reduce markers by zoom and visible option
  private reduceMarkersList<T extends PositionedDeviceData | Position>(markers: T[], total: number, visibleRegion: LatLngBounds): T[] {
    const delimeter = Math.floor(total / this.visible);

    if (delimeter <= 1) {
      return markers;
    }

    return this.showMarkersInVisibleRegion<T>(markers, visibleRegion);
  }

  private thinOutMarkers<T extends PositionedDeviceData | Position>(markers: T[], total: number): T[] {
    const delimeter = Math.floor(total / this.visible);

    if (delimeter <= 1) {
      return markers;
    }

    const visible: T[] = [];

    for (let index = 0; index <= total; index++) {
      if ((index % delimeter) === 0 && markers[index]) {
        visible.push(markers[index]);
      }
    }

    return visible;
  }

  private showMarkersInVisibleRegion<T extends PositionedDeviceData | Position>(devices: T[], visibleRegion: LatLngBounds): T[] {
    const visible: T[] = [];

    for (const device of devices) {
      const contains =
        visibleRegion.northeast.lat >= (device.lat || 0) &&
        visibleRegion.southwest.lat <= (device.lat || 0) &&
        visibleRegion.northeast.lng >= (device.lon || 0) &&
        visibleRegion.southwest.lng <= (device.lon || 0);

      if (contains) {
        visible.push(device);
      }
    }

    if (this.map.getZoom() > 20) {
      return visible;
    }

    return this.thinOutMarkers(visible, visible.length);
  }

  public bound(latLon: LatLon[]) {
    this.map.bound(latLon);
  }

  public setCenter(lat: number, lon: number, zoom?: number) {
    this.map.setCenter(lat, lon, zoom);
  }

  public getCenter() {
    return this.map.getCenter();
  }

  public async drawDotInMapCenter() {
    await this.map.drawDotInMapCenter();
  }

  public drawPolyline(path?: google.maps.LatLng[]) {
    if (path) {
      const convertedPath: LatLng[] = path.map((p) => {
        const nativePath: LatLng = {
          lat: p.lat(),
          lng: p.lng(),
        };
        return nativePath;
      });
      this.map.drawPolyline(convertedPath);
    }
  }

  public removePolyline() {
    this.map.deletePolyline();
  }

  public clearMap() {
    this.map.clearMap();
  }

  public getMarkerOptions(key: string, latLon: LatLon, color: string): CustomMarker {
    return {
      key,
      latLon,
      options: {
        color,
      },
    };
  }
}
